import { useMemo } from 'react';
import type { IntegrationsResponse } from '@/types';
import type { StripeConnectStatus } from '@/lib/api/payments/types';
import { STRIPE_ACCOUNT_STATUS } from '@/lib/constants/paymentStatus';

const ADYEN_STATUS = {
    CANCELLED: 0,
    ACTIVE: 1,
    ERRORED: 2,
} as const;

export const usePaymentStatus = (
    userIntegrations: IntegrationsResponse | undefined,
    hasProducts: boolean,
    stripeConnectStatus?: StripeConnectStatus
) => {
    return useMemo(() => {
        const stripe = userIntegrations?.['stripe-connect'];
        const paypal = userIntegrations?.['paypal'];
        const adyen = userIntegrations?.['adyen'];

        const accountStatusObj = stripeConnectStatus?.account_status;
        const accountStatus = accountStatusObj?.status;
        const statusDetails = accountStatusObj?.status_details || 'You can accept payments and receive payouts.';

        return {
            // Connection status
            isStripeConnected: stripe?.status === 1,
            isPaypalConnected: paypal?.status === 1,
            isAdyenConnected: adyen?.status === ADYEN_STATUS.ACTIVE,

            // Stripe details
            stripe: {
                accountStatus,
                statusDetails,
                isOkay:
                    accountStatus === STRIPE_ACCOUNT_STATUS.ACTIVE ||
                    accountStatus === STRIPE_ACCOUNT_STATUS.PENDING_VERIFICATION,
                isRestricted:
                    accountStatus === STRIPE_ACCOUNT_STATUS.RESTRICTED ||
                    accountStatus === STRIPE_ACCOUNT_STATUS.RESTRICTED_SOON,
                isRejected:
                    accountStatus === STRIPE_ACCOUNT_STATUS.REJECTED ||
                    accountStatus === STRIPE_ACCOUNT_STATUS.REJECTED_APPEAL_SUBMITTED,
                isAppealSubmitted: accountStatus === STRIPE_ACCOUNT_STATUS.REJECTED_APPEAL_SUBMITTED,
                allowedToRestart: stripeConnectStatus?.allow_to_restart || false,
                chargesEnabled: stripeConnectStatus?.charges_enabled,
                showStatus: !!accountStatus && !stripeConnectStatus?.allow_to_restart,
            },

            // PayPal details
            paypal: {
                integrationId: paypal?.integration_id,
                isRestricted: !paypal?.data?.payments_receivable || !paypal?.data?.primary_email_confirmed,
                isRestrictedDueToEmail: !paypal?.data?.primary_email_confirmed,
            },

            // Adyen details
            adyen: {
                integrationId: adyen?.integration_id,
            },

            // Warnings - show if no payment processor connected and has products
            showPaymentWarning: !stripe && !paypal && !adyen && hasProducts,
        };
    }, [userIntegrations, hasProducts, stripeConnectStatus]);
};
